'use client';

import type { IconsWithTextType } from '@eliadmin/core/src/api/general/types/general-types';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ProductImages } from './product-images';
import { ProductIcons } from './product-icons';
import { Breadcrumbs } from '../../homepage/breadcrumbs';
import { ProductDetailBadges } from './product-detail-badges';
import { Heading } from '../../ui/heading';
import { Paragraph } from '../../ui/paragraph';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { ProductVariants } from './variants/product-variants';
import { ProductDetailPrice } from './product-detail-price';
import { ProductDetailAvailability } from './product-detail-availability';
import { ProductQuantity } from './product-quantity';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { ProductPaymentMethods } from './product-payment-methods';
import { ProductDetailCategories } from './product-detail-categories';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { ReactNode } from 'react';

export function ProductInfo({
  icons = [],
  iconsSlot,
}: {
  icons?: IconsWithTextType[];
  iconsSlot?: ReactNode;
}) {
  const { t } = useTranslations();
  const product = useVariantsStore(state => state.product) as ProductDetailData;
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const rating = product.rating ?? 0;
  const ratingCount = product.ratingCount ?? 0;

  const desiredObject =
    product?.variants && product.variants.length > 0 ? selectedVariant : product;
  const ean = desiredObject?.ean?.trim();
  const partNumber = desiredObject?.ext_code?.trim();

  return (
    <section className={cn('custom-section', 'py-6 sm:py-8 md:py-16 lg:py-20 xl:py-24')}>
      <div
        className={cn(
          'custom-container',
          'gap flex flex-col items-start justify-start gap-6 md:flex-row md:justify-between lg:gap-8',
        )}
      >
        <div className="flex w-full flex-col items-center justify-center gap-8 md:max-w-[720px]">
          <CarouselProvider>
            <ProductImages priorityFirstImage />
          </CarouselProvider>
          {iconsSlot !== undefined ? (
            <div className="hidden min-h-40 w-full md:block">{iconsSlot}</div>
          ) : (
            <ProductIcons icons={icons} className="hidden md:grid" />
          )}
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-6 sm:gap-7 md:max-w-[648px] lg:gap-8">
          <div className="flex w-full flex-col items-start justify-start gap-3 sm:gap-4">
            <Breadcrumbs breadcrumb={product.breadcrumb} />
            <ProductDetailBadges product={product} />
            {/* The product page had no <h1>, and line-clamp-2 cut long product names. */}
            <Heading title={selectedVariant?.name ?? product?.name} tag="h1" styleAs="h4" />
            <div className="flex w-full flex-wrap items-center justify-start gap-x-4 gap-y-1">
              <Paragraph color="muted-foreground" size="lg">
                {t('text228')} {desiredObject?.code}
              </Paragraph>
              {/* EAN and PN (external catalogue code) are international abbreviations, not translated. */}
              {partNumber && (
                <Paragraph color="muted-foreground" size="lg">
                  PN: {partNumber}
                </Paragraph>
              )}
              {ean && (
                <Paragraph color="muted-foreground" size="lg">
                  EAN: {ean}
                </Paragraph>
              )}
            </div>
            {ratingCount > 0 && <ProductRating rating={rating} ratingCount={ratingCount} />}
          </div>
          <ProductVariants />
          <div className="ea-product-detail-price-stock flex w-full items-start justify-between gap-4 md:contents">
            <div className="min-w-0 flex-1 md:contents">
              <ProductDetailPrice />
            </div>
            {desiredObject?.availability && (
              <div className="max-w-[45%] shrink-0 md:contents">
                <ProductDetailAvailability
                  availability={desiredObject?.availability}
                  status={product.status}
                />
              </div>
            )}
          </div>
          <ProductQuantity />
          <ProductPaymentMethods />
          <Separator />
          <ProductDetailCategories categories={product.categories} />
        </div>
      </div>
    </section>
  );
}
